import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import type { Facility } from '../../pages/settings/facilities/useFacility';

/**
 * Bascule de l'établissement consulté par un super admin, SANS usurper de
 * compte : le jeton reste le sien, seul l'en-tête X-Facility-Id change.
 *
 * Partagé par le sélecteur d'établissement et la recherche d'hôtel du
 * back-office (« Voir en vue client »).
 */
export function useSwitchToFacility() {
    const { t } = useTranslation('navigation');
    const { currentFacility, setCurrentFacility, setNotifications } = useStateContext();
    const [pendingId, setPendingId] = useState<number | null>(null);

    /** @returns vrai si l'établissement consulté est bien `facilityId` à la fin. */
    const switchTo = async (facilityId: number): Promise<boolean> => {
        if (facilityId === currentFacility?.id) return true;

        setPendingId(facilityId);
        try {
            // La recherche ne renvoie qu'une ligne minimale : le reste de l'app
            // attend l'établissement complet (langues, devise, enfants d'un groupe).
            const { data } = await axiosClient.get<Facility>(`/facilities/${facilityId}`);
            await setCurrentFacility(data);
            return true;
        } catch {
            setNotifications({ error: t('facility_switcher.switch_error') });
            return false;
        } finally {
            setPendingId(null);
        }
    };

    return { switchTo, pendingId };
}
